/**
 * Shared validation schemas for Survey Creation
 *
 * Features:
 * - Generic, reusable validation logic
 * - Type-safe with strict TypeScript
 * - Preserves unique functionality per survey type
 * - Eliminates code duplication while maintaining customizability
 */

import * as yup from 'yup';

/**
 * Shared notification settings validation schema
 * Used by: SatisfactionSurvey, CustomSurvey, EventRegistrationSurvey
 *
 * Critical differences preserved:
 * - CustomSurvey: Checks for empty strings first
 * - SatisfactionSurvey: Has ratings validation (1-5 stars)
 * - EventRegistrationSurvey: Standard email validation only
 */
export const createNotificationSettingsSchema = (options?: {
	includeRatings?: boolean;
	ratingRange?: { min: number; max: number };
}) => {
	const baseSchema = {
		isEnabled: yup.boolean().optional().default(false),
		additionalRecipients: yup
			.array()
			.of(yup.string().required('Email is required').email('Please enter a valid email address'))
			.test('additionalRecipients-validation', function (value) {
				const { isEnabled } = this.parent;
				if (!isEnabled) {
					return true;
				}

				// Check for empty strings first (CustomSurvey requirement)
				const hasEmptyStrings = value.some(recipient => !recipient || recipient.trim() === '');
				if (hasEmptyStrings) {
					return this.createError({ message: 'Please fill in all email addresses' });
				}

				// Shared email validation logic
				const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
				const validEmails = value.filter(
					recipient => recipient && recipient.trim() && emailRegex.test(recipient.trim())
				);

				if (validEmails.length !== value.length) {
					return this.createError({ message: 'Please enter valid email addresses for all recipients' });
				}

				return true;
			}),
	};

	// Add ratings validation for SatisfactionSurvey
	if (options?.includeRatings) {
		const { min = 1, max = 5 } = options.ratingRange || {};
		return yup.object({
			...baseSchema,
			ratings: yup
				.array()
				.of(yup.number())
				.test('ratings-length', `Ratings must be between ${max} and ${min}`, function (val) {
					const { isEnabled } = this.parent || {};
					if (!isEnabled) {
						return true;
					}
					return val.length >= min && val.length <= max;
				}),
		});
	}

	return yup.object(baseSchema);
};

/**
 * Shared tag generation settings validation schema
 * Used by: SatisfactionSurvey, CustomSurvey, EventRegistrationSurvey
 *
 * 100% identical across all survey types - perfect for sharing
 */
export const createTagGenerationSettingsSchema = () =>
	yup.object({
		tagOnCompletion: yup.boolean().optional().default(false),
		completionTag: yup.string().test('completionTag-validation', function (value) {
			const { tagOnCompletion } = this.parent;
			if (!tagOnCompletion) {
				return true;
			}
			if (!value || value.trim() === '') {
				return this.createError({ message: 'Completion tag is required' });
			}
			return true;
		}),
		customFormFieldTagSettings: yup
			.array()
			.of(
				yup.object({
					fieldId: yup.string().required('Field ID is required'),
					shouldGenerateTag: yup.boolean().required('Should generate tag is required'),
					tagPrefix: yup.string().required('Tag prefix is required'),
				})
			)
			.optional()
			.default([]),
	});

/**
 * Shared custom form field validation factory
 * Used by: SatisfactionSurvey, CustomSurvey, EventRegistrationSurvey
 *
 * Preserves unique requirements:
 * - SatisfactionSurvey: 20 question limit, required labels
 * - CustomSurvey: Flexible validation, optional labels
 * - EventRegistrationSurvey: Standard validation, required labels
 */
export const createCustomFormFieldSchema = (options?: {
	questionLimit?: number;
	requireLabels?: boolean;
	/**
	 * Skip all label validation for these fields entirely. Used by V2 (sectioned)
	 * CustomSurveys, where `customForm.fields` is a derived mirror rebuilt from
	 * `surveyForm.surveyParts` only at save time — so its labels are stale/empty
	 * during editing. Question labels for V2 are validated on `surveyParts`
	 * instead. Without this, the mirror's empty labels throw a false
	 * "Question label is required" / "This field is required" that blocks save.
	 *
	 * Distinct from `requireLabels: false` (used by DonationSurvey), which still
	 * requires a non-empty value for String fields via the options-length test.
	 */
	skipLabelValidation?: boolean;
}) => {
	const { questionLimit, requireLabels = true, skipLabelValidation = false } = options || {};

	const fieldSchema = yup.object({
		fieldType: yup.mixed().oneOf(Object.values(['String', 'Option', 'MultipleOptions', 'FileUpload'] as const)),
		label: requireLabels && !skipLabelValidation ? yup.string().required('Question label is required') : yup.string(),
		id: yup.string().optional(),
		isOptional: yup.boolean().optional(),
		isHidden: yup.boolean().optional(),
		options: yup
			.array()
			.of(yup.object({ label: yup.string(), fieldId: yup.string() }))
			.test('options-length-test', function (val) {
				const { fieldType, label } = this.parent;

				// Option/MultipleOption fields require at least 2 options
				if (
					val?.every(x => Boolean(x?.label)) &&
					val?.length >= 2 &&
					(fieldType === 'Option' || fieldType === 'MultipleOptions')
				) {
					return true;
				}

				// String fields are valid without options
				if (fieldType === 'String') {
					// CustomSurvey specific: check label requirement for String fields.
					// Skipped entirely for V2 (skipLabelValidation), whose mirror labels
					// are stale/empty and validated on surveyParts instead.
					if (!skipLabelValidation && !requireLabels && !label) {
						return this.createError({ message: 'This field is required' });
					}
					return true;
				}

				if (fieldType === 'FileUpload') {
					return true;
				}

				return this.createError({ message: 'This field and its options are required' });
			}),
	});

	const fieldsArraySchema = yup.array().of(fieldSchema);

	// Apply question limit if specified (SatisfactionSurvey: 20, others: unlimited)
	if (questionLimit) {
		return fieldsArraySchema.max(questionLimit, `Maximum ${questionLimit} questions allowed`);
	}

	return fieldsArraySchema;
};

/**
 * Shared base survey validation schema
 * Contains common fields across all survey types
 */
export const createBaseSurveySchema = (options?: {
	requireIntro?: boolean;
	requireStartDate?: boolean;
	requireExpirationDate?: boolean;
}) => {
	const { requireIntro = false, requireStartDate = false, requireExpirationDate = false } = options || {};

	return {
		name: yup.string().required('Survey name is required'),
		startDate: requireStartDate ? yup.string().required('Start date is required') : yup.string().optional(),
		expirationDate: requireExpirationDate
			? yup.string().when('indefinitely', (indefinitely, schema) => {
					return !indefinitely ? schema.required('Expiration date is required') : schema.notRequired();
				})
			: yup.string().optional(),
		indefinitely: yup.boolean().optional().default(false),
		customFormIsDisabled: yup.boolean().optional(),
		intro: requireIntro
			? yup
					.string()
					.required('Survey intro is required')
					.test('intro-length', 'Survey intro is required', value => Boolean(value?.length))
			: yup.mixed().optional(), // Allow IRichContentEditorState for CustomSurvey
	};
};

/**
 * Factory for creating complete survey validation schemas
 * Combines base schema with survey-specific extensions
 */
export const createSurveyValidationSchema = (
	baseOptions?: Parameters<typeof createBaseSurveySchema>[0],
	extensions?: Record<string, yup.AnySchema>
) => {
	const baseSchema = createBaseSurveySchema(baseOptions);

	return yup.object({
		...baseSchema,
		...extensions,
	});
};

/**
 * Schema for a single V2 SurveyQuestion. The discriminator is `_type` —
 * Dropdown and CheckBoxes require >= 2 valid options; ShortAnswer and FileUpload
 * have no options requirement.
 */
export function buildSurveyQuestionSchema(opts: { requireLabels?: boolean } = {}) {
	const { requireLabels = true } = opts;
	return yup.lazy((value: { _type?: string }) => {
		const base = yup.object({
			id: yup.string().required(),
			fieldId: yup.string().required(),
			label: requireLabels ? yup.string().required('Question label is required') : yup.string(),
			isOptional: yup.boolean().optional(),
			// Match the legacy 'visibleWhen' shape — we don't need to deeply validate it here.
			// `nullable()`: V1's buildSurveyForm explicitly sets visibleWhen=null
			// on questions without a rule. Without `nullable()` yup rejects null
			// with "cannot be null", which lit up Section 1 red on V1→V2 save.
			visibleWhen: yup.mixed().nullable().optional(),
		});
		if (value?._type === 'DropDownQuestion' || value?._type === 'CheckBoxesQuestion') {
			return base.shape({
				options: yup
					.array()
					.of(yup.object({ id: yup.string().optional(), label: yup.string().optional() }))
					.test(
						'options-min',
						'At least 2 options are required',
						val => (val ?? []).filter(o => o?.label?.trim()).length >= 2
					),
			});
		}
		return base;
	});
}

/**
 * Schema for the V2 `surveyForm.surveyParts` array — accepts a mix of
 * `SurveySection` parts (which themselves nest a questions array) and bare
 * `SurveyQuestion` parts (legacy V1-at-root shape under the V2 wrapper).
 */
export function buildSurveyPartsSchema(opts: { requireLabels?: boolean } = {}) {
	const questionSchema = buildSurveyQuestionSchema(opts);
	return yup.array().of(
		yup.lazy((value: { _type?: string }) => {
			if (value?._type === 'SurveySection') {
				return yup.object({
					_type: yup.string().oneOf(['SurveySection']).required(),
					id: yup.string().required(),
					title: yup.string().optional(),
					description: yup.string().optional(),
					questions: yup.array().of(questionSchema).min(1, 'Section must contain at least one question'),
					// `nullable()`: V1's buildSurveyForm explicitly sets visibleWhen=null
					// on questions without a rule. Without `nullable()` yup rejects null
					// with "cannot be null", which lit up Section 1 red on V1→V2 save.
					visibleWhen: yup.mixed().nullable().optional(),
				});
			}
			return questionSchema;
		})
	);
}
